import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Share2, Image, Instagram } from 'lucide-react';

const DayCardActions = ({ day, month, year, label, description, emoji }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  // Función para generar imagen en servidor y obtener URL pública
  const generatePublicImageURL = async () => {
    try {
      setIsGeneratingImage(true);
      
      const response = await fetch('/api/share/generate-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          day,
          month,
          year,
          label,
          description,
          emoji,
        }),
      });

      if (!response.ok) {
        throw new Error('Error generando imagen');
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('Error:', error);
      alert('⚠️ No se pudo generar la imagen. Por favor, intenta de nuevo.');
      return null;
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Función para compartir con URL pública (Facebook, WhatsApp)
  const shareWithPublicURL = async (platform) => {
    setShowShareModal(false);
    
    // Mostrar indicador de carga
    const loadingAlert = alert('⏳ Generando imagen...\n\nEsto tomará unos segundos.');
    
    // Generar imagen y obtener URL
    const imageURL = await generatePublicImageURL();
    
    if (!imageURL) {
      return; // Ya se mostró error en generatePublicImageURL
    }

    // Formatear fecha
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 
                   'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    const fechaFormateada = `${day} de ${meses[month - 1]} ${year}`;

    if (platform === 'facebook') {
      // Facebook: Compartir URL con Facebook Sharer
      const facebookURL = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(imageURL)}`;
      
      alert(
        '✅ ¡Imagen lista!\n\n' +
        '📘 Se abrirá Facebook con tu imagen.\n' +
        'Solo haz click en "Publicar" 🎉'
      );
      
      window.open(facebookURL, '_blank', 'width=600,height=400');
    } else if (platform === 'whatsapp') {
      // WhatsApp: Compartir URL + texto
      const text = `${emoji} ¡Hoy ${fechaFormateada} es ${label}!\n\n💜 ${description.split('.')[0]}.\n\n🔮 Descubre tu energía personal:\n👉 ${imageURL}`;
      const whatsappURL = `https://wa.me/?text=${encodeURIComponent(text)}`;
      
      alert(
        '✅ ¡Imagen lista!\n\n' +
        '💚 Se abrirá WhatsApp con tu mensaje e imagen.\n' +
        'Solo haz click en "Enviar" 🎉'
      );
      
      window.open(whatsappURL, '_blank');
    }
  };

  // Función para agregar un día específico a Google Calendar
  const addToGoogleCalendar = () => {
    // Formatear fecha para Google Calendar: YYYYMMDD (sin guiones, sin hora)
    // Importante: Usar valores directos sin crear Date para evitar problemas de zona horaria
    const yearStr = year.toString();
    const monthStr = month.toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    const formattedDate = `${yearStr}${monthStr}${dayStr}`; // Ejemplo: 20251104
    
    const title = encodeURIComponent(`${emoji} ${label}`);
    const details = encodeURIComponent(`Día numerológico importante: ${description}\n\nConoce más en Numerathum - Mi Brújula del Tiempo`);
    
    // Formato para evento de día completo: YYYYMMDD/YYYYMMDD
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formattedDate}/${formattedDate}&details=${details}`;
    window.open(url, '_blank');
  };

  // Función para generar imagen compartible con Canvas API
  // platform puede ser: 'facebook', 'instagram', 'whatsapp', o null (compartir genérico)
  const generateShareImage = async (platform = null) => {
    try {
      // Crear canvas de 1080x1080 (formato cuadrado para redes sociales)
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1080;
      const ctx = canvas.getContext('2d');
      
      // Formatear la fecha en español
      const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 
                     'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
      const fechaFormateada = `${day} de ${meses[month - 1]} ${year}`;
      
      // Fondo morado sólido (branding Numerathum); el canvas no entiende var(), se lee de :root
      const morado = getComputedStyle(document.documentElement).getPropertyValue('--color-morado').trim() || '#693061';
      ctx.fillStyle = morado;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Cargar y dibujar el logo (favicon)
      const logo = new window.Image();
      logo.crossOrigin = 'anonymous';
      logo.src = '/favicon_nume.png';
      
      await new Promise((resolve, reject) => {
        logo.onload = resolve;
        logo.onerror = reject;
      });
      
      // Dibujar logo en la parte superior
      const logoSize = 120;
      ctx.drawImage(logo, (canvas.width - logoSize) / 2, 80, logoSize, logoSize);
      
      // Texto: NUMERATHUM (título principal)
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 56px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('NUMERATHUM', canvas.width / 2, 260);
      
      // Subtítulo: Mi Brújula del Tiempo
      ctx.font = '28px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#f0e2ee'; // Morado muy claro
      ctx.fillText('Mi Brújula del Tiempo', canvas.width / 2, 310);
      
      // Fecha
      ctx.font = '32px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(`Hoy, ${fechaFormateada}`, canvas.width / 2, 380);
      
      // Título del día numerológico con subrayado elegante
      const titleY = 470;
      
      // Texto del título
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 48px system-ui, -apple-system, sans-serif';
      ctx.fillText(label, canvas.width / 2, titleY);
      
      // Medir el ancho del texto para el subrayado
      const textMetrics = ctx.measureText(label);
      const textWidth = textMetrics.width;
      
      // Subrayado decorativo (línea blanca debajo del texto)
      const underlineY = titleY + 15;
      const underlineStartX = (canvas.width - textWidth) / 2;
      const underlineEndX = (canvas.width + textWidth) / 2;
      
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(underlineStartX, underlineY);
      ctx.lineTo(underlineEndX, underlineY);
      ctx.stroke();
      
      // Emoji y título
      ctx.font = '72px system-ui, -apple-system, sans-serif';
      ctx.fillText(emoji, canvas.width / 2, 620);
      
      // Descripción (dividida en líneas)
      ctx.font = '28px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#f7eef6'; // Casi blanco con tinte morado
      const maxWidth = 900;
      const lineHeight = 42;
      let yPosition = 720;
      
      // Dividir descripción en palabras y crear líneas que no excedan maxWidth
      const words = description.split(' ');
      let currentLine = '';
      
      for (let i = 0; i < words.length; i++) {
        const testLine = currentLine + words[i] + ' ';
        const metrics = ctx.measureText(testLine);
        
        if (metrics.width > maxWidth && currentLine !== '') {
          ctx.fillText(currentLine.trim(), canvas.width / 2, yPosition);
          currentLine = words[i] + ' ';
          yPosition += lineHeight;
          
          // Limitar a 3 líneas máximo
          if (yPosition > 800) break;
        } else {
          currentLine = testLine;
        }
      }
      
      // Última línea
      if (currentLine.trim() !== '' && yPosition <= 800) {
        ctx.fillText(currentLine.trim(), canvas.width / 2, yPosition);
      }
      
      // URL del sitio web
      ctx.font = 'bold 32px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText('numerathum.numerologia-cotidiana.com', canvas.width / 2, 950);
      
      // Convertir canvas a blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      
      // Cerrar modal si estaba abierto
      setShowShareModal(false);
      
      // Comportamiento según la plataforma
      if (platform === 'facebook') {
        // Facebook: Copiar al portapapeles y abrir Facebook
        try {
          if (navigator.clipboard && window.ClipboardItem) {
            // 1. Copiar la imagen primero
            const clipboardItem = new window.ClipboardItem({ 'image/png': blob });
            await navigator.clipboard.write([clipboardItem]);
            
            // 2. Preparar texto sugerido
            const suggestedText = `${emoji} ¡Hoy ${fechaFormateada} es ${label}!\n\n💜 ${description.split('.')[0]}.\n\n🔮 Descubre tu energía personal en:\n👉 https://numerathum.numerologia-cotidiana.com\n\n#Numerathum #Numerología #EnergíaDelDía`;
            
            // 3. Preguntar si quiere copiar el texto también
            const copyText = confirm(
              '✅ Imagen copiada al portapapeles\n\n' +
              '📘 PASO 1: En Facebook, pega la imagen (Ctrl+V / Cmd+V)\n\n' +
              '📝 PASO 2: ¿Quieres copiar también el texto sugerido para tu publicación?\n\n' +
              '(Presiona OK para copiar el texto, o Cancelar para solo usar la imagen)'
            );
            
            if (copyText) {
              // Copiar el texto al portapapeles
              await navigator.clipboard.writeText(suggestedText);
              alert(
                '✅ ¡Texto también copiado!\n\n' +
                '📘 Ahora en Facebook:\n' +
                '1. Pega la imagen (Ctrl+V)\n' +
                '2. Pega el texto (Ctrl+V otra vez)\n' +
                '3. ¡Publica! 🎉\n\n' +
                'Se abrirá Facebook en una nueva pestaña.'
              );
            } else {
              alert(
                '✅ Imagen lista\n\n' +
                '📘 Ve a Facebook y pega la imagen (Ctrl+V / Cmd+V)\n\n' +
                'Se abrirá Facebook en una nueva pestaña.'
              );
            }
            
            // 4. Abrir Facebook
            window.open('https://www.facebook.com/', '_blank');
          } else {
            throw new Error('Clipboard API no soportada');
          }
        } catch (error) {
          console.error('Error copiando al portapapeles:', error);
          // Fallback: descargar
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `numerathum-facebook-${label.replace(/\s+/g, '-').toLowerCase()}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          alert('📸 Imagen descargada. Súbela manualmente a Facebook.');
        }
      } else if (platform === 'instagram') {
        // Instagram: Solo descargar (Instagram no tiene API web)
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `numerathum-instagram-${label.replace(/\s+/g, '-').toLowerCase()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        alert(
          '📸 Imagen descargada\n\n' +
          '📱 Para compartir en Instagram:\n' +
          '1. Abre Instagram en tu móvil\n' +
          '2. Crea una nueva publicación o historia\n' +
          '3. Selecciona la imagen descargada\n\n' +
          '¡Listo! 🎉'
        );
      } else if (platform === 'whatsapp') {
        // WhatsApp: Copiar al portapapeles y abrir WhatsApp Web
        try {
          if (navigator.clipboard && window.ClipboardItem) {
            // 1. Copiar la imagen primero
            const clipboardItem = new window.ClipboardItem({ 'image/png': blob });
            await navigator.clipboard.write([clipboardItem]);
            
            // 2. Preparar texto sugerido (más corto para WhatsApp)
            const suggestedText = `${emoji} ¡Hoy ${fechaFormateada} es ${label}!\n\n💜 ${description.split('.')[0]}.\n\n🔮 Descubre tu energía personal:\n👉 https://numerathum.numerologia-cotidiana.com`;
            
            // 3. Preguntar si quiere copiar el texto también
            const copyText = confirm(
              '✅ Imagen copiada al portapapeles\n\n' +
              '💚 PASO 1: En WhatsApp, pega la imagen (Ctrl+V / Cmd+V)\n\n' +
              '📝 PASO 2: ¿Quieres copiar también el texto para acompañar la imagen?\n\n' +
              '(Presiona OK para copiar el texto, o Cancelar para solo enviar la imagen)'
            );
            
            if (copyText) {
              // Copiar el texto al portapapeles
              await navigator.clipboard.writeText(suggestedText);
              alert(
                '✅ ¡Texto también copiado!\n\n' +
                '💚 Ahora en WhatsApp:\n' +
                '1. Pega la imagen (Ctrl+V)\n' +
                '2. Pega el texto (Ctrl+V otra vez)\n' +
                '3. ¡Envía! 🎉\n\n' +
                'Se abrirá WhatsApp Web en una nueva pestaña.'
              );
            } else {
              alert(
                '✅ Imagen lista\n\n' +
                '💚 Ve a WhatsApp y pega la imagen (Ctrl+V / Cmd+V)\n\n' +
                'Se abrirá WhatsApp Web en una nueva pestaña.'
              );
            }
            
            // 4. Abrir WhatsApp Web
            window.open('https://web.whatsapp.com/', '_blank');
          } else {
            throw new Error('Clipboard API no soportada');
          }
        } catch (error) {
          console.error('Error copiando al portapapeles:', error);
          // Fallback: descargar
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `numerathum-whatsapp-${label.replace(/\s+/g, '-').toLowerCase()}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          alert('📸 Imagen descargada. Envíala manualmente por WhatsApp.');
        }
      } else {
        // Sin plataforma específica: Compartir genérico (móviles) o copiar al portapapeles (desktop)
        if (navigator.share && navigator.canShare?.({ files: [new File([blob], 'numerathum-dia.png', { type: 'image/png' })] })) {
          const file = new File([blob], `numerathum-${label.replace(/\s+/g, '-').toLowerCase()}.png`, { type: 'image/png' });
          await navigator.share({
            title: `${emoji} ${label} - Numerathum`,
            text: `¡Hoy ${fechaFormateada} es ${label}! 💜`,
            files: [file]
          });
        } else {
          // Desktop: copiar al portapapeles
          try {
            if (navigator.clipboard && window.ClipboardItem) {
              const clipboardItem = new window.ClipboardItem({ 'image/png': blob });
              await navigator.clipboard.write([clipboardItem]);
              
              const shouldDownload = confirm(
                '✅ Imagen copiada al portapapeles\n\n' +
                '📱 Ahora puedes pegarla (Ctrl+V / Cmd+V) donde quieras.\n\n' +
                '💾 ¿También quieres descargar el archivo?'
              );
              
              if (shouldDownload) {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `numerathum-${label.replace(/\s+/g, '-').toLowerCase()}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
              }
            } else {
              throw new Error('Clipboard API no soportada');
            }
          } catch (clipboardError) {
            console.log('No se pudo copiar al portapapeles, descargando...', clipboardError);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `numerathum-${label.replace(/\s+/g, '-').toLowerCase()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            alert('📸 Imagen descargada. Compártela donde quieras.');
          }
        }
      }
      
    } catch (error) {
      console.error('Error generando imagen:', error);
      alert('⚠️ No se pudo generar la imagen. Por favor, intenta de nuevo.');
    }
  };

  // Función para compartir un día específico (texto mejorado)
  const shareDay = () => {
    // Formatear la fecha en español
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 
                   'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    const fechaFormateada = `${day} de ${meses[month - 1]} ${year}`;
    
    // Extraer el número del día del label si existe (ej: "Día 11" -> "11")
    const diaNumerico = label.match(/\d+/)?.[0] || '';
    
    const text = `${emoji} ¡Hoy ${fechaFormateada} es ${label}!

💜 ${description.split('.')[0]}.

🔮 Descubre tu energía personal en:
👉 https://numerathum.numerologia-cotidiana.com

#Numerathum #Numerología #EnergíaDelDía`;
    
    if (navigator.share) {
      // Usar Web Share API si está disponible (móviles)
      navigator.share({
        title: `${emoji} ${label} - Numerathum`,
        text: text,
      }).catch(() => {
        // Si el usuario cancela, no hacer nada
      });
    } else {
      // Fallback: copiar al portapapeles (desktop)
      navigator.clipboard.writeText(text).then(() => {
        alert('✅ Texto copiado al portapapeles\n\n¡Pégalo en WhatsApp, Telegram o donde quieras compartirlo!');
      }).catch(() => {
        alert('⚠️ No se pudo copiar automáticamente. Por favor, selecciona y copia el texto manualmente.');
      });
    }
  };

  return (
    <>
      <div className="flex items-center gap-1">
        <button
          onClick={addToGoogleCalendar}
          className="p-1.5 rounded-full bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
          title="Agregar a Google Calendar"
        >
          <Calendar size={12} />
        </button>
        
        <button
          onClick={shareDay}
          className="p-1.5 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
          title="Compartir texto"
        >
          <Share2 size={12} />
        </button>
        
        <button
          onClick={() => setShowShareModal(true)}
          className="p-1.5 rounded-full bg-morado/5 text-morado hover:bg-morado/10 transition-colors"
          title="Compartir imagen en redes sociales"
        >
          <Image size={12} />
        </button>
      </div>

      {/* Modal de compartir imagen */}
      {showShareModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowShareModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
              Compartir Días Importantes
            </h3>
            
            <div className="space-y-3">
              <button
                onClick={() => shareWithPublicURL('facebook')}
                disabled={isGeneratingImage}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span>Compartir en Facebook</span>
              </button>
              
              <button
                onClick={() => generateShareImage('instagram')}
                disabled={isGeneratingImage}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-pink-50 text-pink-600 hover:bg-pink-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Instagram size={20} />
                <span>Descargar para Instagram</span>
              </button>
              
              <button
                onClick={() => shareWithPublicURL('whatsapp')}
                disabled={isGeneratingImage}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-green-50 text-green-600 hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                <span>Compartir en WhatsApp</span>
              </button>
            </div>
            
            <button
              onClick={() => setShowShareModal(false)}
              className="w-full mt-4 px-4 py-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancelar
            </button>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default DayCardActions;
